import React, { ReactNode } from 'react';
import { Toast as FlowBiteToast, ToastProps as FlowBiteToastProps } from 'flowbite-react';
import { ToastTheme } from '../../styles/themes/toast';
import { IconCheckCircle, IconErrorWarning, IconInfo, IconWarning } from '../../icons';
import { IconLoader } from '../iconLoader';
import { Text } from '../text';
// the app-import-free slice of `src/i18n` (see i18n-context.tsx)
import { useTranslations } from 'i18n/use-translations';

export type ToastVariant = 'info' | 'alert' | 'success' | 'warning';

export type ToastProps = Omit<FlowBiteToastProps, 'style' | 'duration'> & {
    ariaLabel?: string;
    testId?: string;
    duration?: number;
    message?: ReactNode;
    key?: string | number;
    type?: ToastVariant;
    description?: ReactNode;
    icon?: React.ReactNode;
    onClose?: () => void;
    closeLabel?: string; // accessible name for the close button
    visible?: boolean;
    /** Make the toast its own live region; ToastProvider turns this off and announces through its own regions. */
    announce?: boolean;
};

const SLIDE_MS = 400;

// handles toast slide-in animation with a plain CSS solution
function slideCSS(lifetimeMs: number) {
    const e = ((SLIDE_MS / lifetimeMs) * 100).toFixed(2);
    const x = (((lifetimeMs - SLIDE_MS) / lifetimeMs) * 100).toFixed(2);

    return `
        @keyframes sl-${lifetimeMs} {
        0%    { transform: translateX(100%); opacity: 0 }
        ${e}% { transform: translateX(0);    opacity: 1 }
        ${x}% { transform: translateX(0);    opacity: 1 }
        100%  { transform: translateX(100%); opacity: 0 }
    }`;
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>((props, ref) => {
    const {
        ariaLabel,
        testId,
        message,
        type,
        description,
        icon,
        duration = 10000,
        onClose,
        closeLabel,
        announce = true,
        ...otherProps
    } = props;

    const isAlert = type === 'alert';
    const liveProps = announce
        ? {
              role: isAlert ? 'alert' : 'status',
              'aria-live': isAlert ? ('assertive' as const) : ('polite' as const),
              'aria-atomic': true,
          }
        : { role: 'group' };

    return (
        <>
            {/* outside the ref'd node, so reading its text back for an announcement leaves the keyframes out */}
            <style>{slideCSS(duration)}</style>
            <div
                ref={ref}
                style={{ animation: `sl-${duration} ${duration}ms ease both` }}
                aria-label={ariaLabel}
                {...liveProps}
            >
                {/* flowbite hardcodes role="alert" on its root, which would nest a second live region */}
                <FlowBiteToast data-testid={testId} role="none" theme={ToastTheme} {...otherProps}>
                    <ToastContent
                        message={message}
                        description={description}
                        duration={duration}
                        icon={icon}
                        type={type}
                        onClose={onClose}
                        closeLabel={closeLabel}
                    />
                </FlowBiteToast>
            </div>
        </>
    );
});
Toast.displayName = 'Toast';

export const ToastContent: React.FC<ToastProps> = (props) => {
    const { icon, message, description, type, onClose, closeLabel } = props;
    const { __ } = useTranslations();

    const startIcon =
        icon !== undefined ? (
            icon
        ) : type === 'success' ? (
            <IconCheckCircle data-testid="toast-icon" className="fill-green-600" width={'24px'} height={'24px'} />
        ) : type === 'alert' ? (
            <IconErrorWarning data-testid="toast-icon" className="fill-red-700" width={'24px'} height={'24px'} />
        ) : type === 'info' ? (
            <IconInfo data-testid="toast-icon" className="fill-blue-700" width={'24px'} height={'24px'} />
        ) : type === 'warning' ? (
            <IconWarning data-testid="toast-icon" className="fill-yellow-500" width={'24px'} height={'24px'} />
        ) : null;
    return (
        <div className="flex flex-1 gap-3" style={{ minWidth: '250px', paddingTop: '8px' }}>
            {startIcon}
            <div className="flex flex-col gap-2 flex-1 text-system-primary text-sm">
                <div className="gap-3 flex content-center">
                    <Text as="div" variant="title-small" className={description ? 'flex-1' : 'flex-1 leading-6'}>
                        {message}
                    </Text>
                    {/* button as reachable by keyboard and announced */}
                    <button
                        type="button"
                        className="hover:primary-hover-state w-[24px] h-[24px] rounded-sm flex items-center justify-center border-none bg-transparent p-0 cursor-pointer"
                        aria-label={closeLabel ?? __('misc.close')}
                        onClick={onClose}
                    >
                        <IconLoader iconName="IconCancel" size="sm" />
                    </button>
                </div>
                <Text as="div" variant="body-medium">
                    {description}
                </Text>
            </div>
        </div>
    );
};
